import { Box, Typography } from '@mui/material'
import type { ReactNode } from 'react'

import { FONT_WEIGHT } from '@/shared/styles/typography'

// 指導前準備のカード (β) の部品. features 層は apps/* の colorTokens を import できないので
// (.eslintrc.cjs no-restricted-imports)、 色は theme の palette で引く:
//   text.primary = fg.default / text.secondary = fg.muted / text.disabled = fg.faint
//   action.selected = bg.muted / secondary.light = bg.emphasis / divider = border.default
//
// 文字は 3 段 (hub design-book ADR-0061):
//   見出し 14/600 text.primary / 本文 14/400 text.primary (BodyText) / 補足 12/400 text.secondary (MetaText)
//   枠付き小片は tag (caption = 12/500). text.disabled は行頭の印と空の値だけ.

export type PrepFace = 'guidance' | 'audit'

// 節. 監査の面は節が 4 つあるので、 見出しを本文と同じ 14px の semibold にして区切る.
// aside は見出しの脇の短い補足 (「監査はまだ」・前回の日付など).
export const PrepSection = ({
  title,
  aside,
  children,
}: {
  title: string
  aside?: ReactNode
  children: ReactNode
}) => (
  <Box component="section" aria-label={title}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        columnGap: 1,
        mb: 1,
      }}
    >
      <Typography
        variant="body2"
        component="h3"
        sx={{
          fontWeight: FONT_WEIGHT.semibold,
          color: 'text.primary',
        }}
      >
        {title}
      </Typography>
      {aside && (
        <Typography
          variant="caption"
          sx={{
            fontWeight: FONT_WEIGHT.regular,
            color: 'text.secondary',
          }}
        >
          {aside}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
)

export const SectionDivider = () => (
  <Box
    sx={{
      borderTop: 1,
      borderColor: 'divider',
    }}
  />
)

// 空の表記 (empty-states の型 1). 既定は「(なし)」.
export const EmptyLine = ({
  children = '(なし)',
}: {
  children?: ReactNode
}) => (
  <Typography
    variant="body2"
    sx={{
      color: 'text.disabled',
    }}
  >
    {children}
  </Typography>
)

// 本文の右の枠付き小片 (監査の要点の禁忌 / 注意・確認要点の関連加算). 字格は tag (caption).
export const InlineTag = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="caption"
    component="span"
    sx={{
      display: 'inline-block',
      ml: 1,
      px: 0.75,
      color: 'text.secondary',
      border: 1,
      borderColor: 'divider',
      borderRadius: '4px',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
    }}
  >
    {children}
  </Typography>
)
